import { useState, useEffect } from 'react'
import { Download, FileSpreadsheet, FileText, RefreshCw, Columns } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { downloadPdf } from '../../utils/pdfUtils'
import leiloesService from '../../services/leiloesService'
import api from '../../api/axios'

const ALL_COLS = ['Lote', 'Descrição', 'Idade', 'Peso', 'Vendedor', 'Pagamento', 'Observações']

export default function CatalogoTransmissao() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes]           = useState([])
  const [leilaoId, setLeilaoId]         = useState('')
  const [rows, setRows]                 = useState([])
  const [loadingL, setLoadingL]         = useState(true)
  const [loadingD, setLoadingD]         = useState(false)
  const [colsSelecionadas, setColsSel]  = useState(new Set(ALL_COLS))
  const [mostrarCols, setMostrarCols]   = useState(false)
  const [semRaca, setSemRaca]           = useState(false)
  const [semTipo, setSemTipo]           = useState(false)
  const [semSexo, setSemSexo]           = useState(false)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        const ativo = sorted.find(l => l.status === 'on') || sorted[0]
        if (ativo) setLeilaoId(String(ativo.id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setRows([]); return }
    setLoadingD(true)
    api.get(`/organizadores/${orgId}/catalogos/leiloes/${leilaoId}/catalogo-transmissao/dados`, { params: { sem_raca: semRaca || undefined, sem_tipo: semTipo || undefined, sem_sexo: semSexo || undefined } })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoadingD(false))
  }, [orgId, leilaoId, semRaca, semTipo, semSexo])

  const toggleCol = (col) => {
    setColsSel(prev => {
      const next = new Set(prev)
      if (next.has(col)) {
        if (next.size === 1) return prev // mínimo 1 coluna
        next.delete(col)
      } else {
        next.add(col)
      }
      return next
    })
  }

  const colsOrdenadas = ALL_COLS.filter(c => colsSelecionadas.has(c))

  const download = (fmt) => {
    const base = (api.defaults.baseURL || '').replace(/\/$/, '')
    const colsParam = colsOrdenadas.join(',')
    const racaParam = semRaca ? '&sem_raca=true' : ''
    const tipoParam = semTipo ? '&sem_tipo=true' : ''
    const sexoParam = semSexo ? '&sem_sexo=true' : ''
    const url = `${base}/organizadores/${orgId}/catalogos/leiloes/${leilaoId}/catalogo-transmissao/${fmt}?colunas=${encodeURIComponent(colsParam)}${racaParam}${tipoParam}${sexoParam}`
    const token = JSON.parse(localStorage.getItem('rematei-auth') || '{}')?.state?.token
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(async blob => {
        const leilao = leiloes.find(l => String(l.id) === leilaoId)
        const nome = leilao?.nome || leilaoId
        const blobUrl = URL.createObjectURL(blob)
        await downloadPdf(blobUrl, `Catalogo-${nome}.${fmt}`)
        URL.revokeObjectURL(blobUrl)
      })
  }

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador.
    </div>
  )

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl px-5 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-xl font-bold text-white">Catálogo Transmissão</h1>
            <p className="text-teal-100 text-sm mt-0.5">Exportar lotes para CSV ou XLSX</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => download('csv')}
              disabled={!rows.length}
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-teal-700 rounded-lg text-sm font-medium shadow transition-colors"
            >
              <FileText size={15} /> Baixar CSV
            </button>
            <button
              onClick={() => download('xlsx')}
              disabled={!rows.length}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium shadow transition-colors"
            >
              <FileSpreadsheet size={15} /> Baixar XLSX
            </button>
          </div>
        </div>
      </div>

      {/* Seletor de leilão + colunas */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-500 font-medium mb-1">Leilão</label>
            <select
              value={leilaoId}
              onChange={e => setLeilaoId(e.target.value)}
              disabled={loadingL}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            >
              {loadingL
                ? <option>Carregando...</option>
                : leiloes.map(l => (
                    <option key={l.id} value={String(l.id)}>
                      {l.nome} — {new Date(l.data_leilao).toLocaleDateString('pt-BR')}
                    </option>
                  ))
              }
            </select>
          </div>
          <button
            onClick={() => {
              setRows([])
              setLoadingD(true)
              api.get(`/organizadores/${orgId}/catalogos/leiloes/${leilaoId}/catalogo-transmissao/dados`, { params: { sem_raca: semRaca || undefined, sem_tipo: semTipo || undefined, sem_sexo: semSexo || undefined } })
                .then(r => setRows(r.data || []))
                .catch(() => setRows([]))
                .finally(() => setLoadingD(false))
            }}
            disabled={!leilaoId || loadingD}
            className="flex items-center gap-2 px-4 py-2 h-9 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            <RefreshCw size={14} className={loadingD ? 'animate-spin' : ''} />
            Atualizar
          </button>
          <button
            onClick={() => setSemSexo(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 h-9 rounded-lg text-sm font-medium border transition-colors ${
              semSexo
                ? 'bg-blue-50 border-blue-400 text-blue-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            title="Remover sexo da descrição ao exportar"
          >
            Sem Sexo
          </button>
          <button
            onClick={() => setMostrarCols(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 h-9 rounded-lg text-sm font-medium border transition-colors ${
              mostrarCols
                ? 'bg-teal-50 border-teal-400 text-teal-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Columns size={14} />
            Colunas ({colsSelecionadas.size}/{ALL_COLS.length})
          </button>
          <button
            onClick={() => setSemRaca(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 h-9 rounded-lg text-sm font-medium border transition-colors ${
              semRaca
                ? 'bg-orange-50 border-orange-400 text-orange-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            title="Remover raça da descrição ao exportar"
          >
            Sem Raça
          </button>
          <button
            onClick={() => setSemTipo(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 h-9 rounded-lg text-sm font-medium border transition-colors ${
              semTipo
                ? 'bg-purple-50 border-purple-400 text-purple-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            title="Remover tipo da descrição ao exportar"
          >
            Sem Tipo
          </button>
        </div>

        {/* Seletor de colunas */}
        {mostrarCols && (
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500 font-medium mb-2">Escolha as colunas a exportar:</p>
            <div className="flex flex-wrap gap-2">
              {ALL_COLS.map(col => {
                const ativa = colsSelecionadas.has(col)
                return (
                  <button
                    key={col}
                    onClick={() => toggleCol(col)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      ativa
                        ? 'bg-teal-600 border-teal-600 text-white'
                        : 'bg-white border-gray-300 text-gray-500 hover:border-teal-400 hover:text-teal-600'
                    }`}
                  >
                    {ativa && (
                      <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {col}
                  </button>
                )
              })}
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setColsSel(new Set(ALL_COLS))}
                className="text-xs text-teal-600 hover:underline"
              >
                Selecionar todas
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => setColsSel(new Set([ALL_COLS[0]]))}
                className="text-xs text-gray-500 hover:underline"
              >
                Somente Lote
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabela preview */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loadingD ? (
          <div className="h-40 flex items-center justify-center">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-teal-600" />
          </div>
        ) : rows.length === 0 ? (
          <div className="h-40 flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
            <FileText size={32} />
            <span>Nenhum lote encontrado</span>
          </div>
        ) : (
          <>
            <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium">
                {rows.length} lote{rows.length !== 1 ? 's' : ''} — {leilaoAtual?.nome}
              </span>
              <span className="text-xs text-gray-400">
                {colsOrdenadas.length} coluna{colsOrdenadas.length !== 1 ? 's' : ''} selecionada{colsOrdenadas.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {colsOrdenadas.map(c => (
                      <th key={c} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {colsOrdenadas.map(c => (
                        <td key={c} className="px-3 py-1.5 text-gray-700 border-b border-gray-100 max-w-[220px] truncate">
                          {row[c] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
